// Katalog 2026 – 49 Schulungsmodule in acht Produktwelten.
// Privacy und Quality sind als Welt angelegt, tragen aber noch keine Module:
// Privacy startet mit PRIV-01 (Datenschutz im Arbeitsalltag, in Produktion),
// Quality mit QM-01 (Qualitaetsbewusstsein ISO 9001, noch nicht produziert).
// Leere Welten sind unkritisch – MODULE_GROUPS (gaShared.tsx) und die
// Demo-Auswahl (GaModules.tsx) filtern Gruppen ohne Module heraus.
// SEC-06 und ENV-06 sind die 2026-08 ergaenzten robusten Kernmodule.
// Erzeugt aus den Modulpaketen unter „SHERLOX AWARE Schulungen/katalog-2026"
// (Sprecherskript, Fragenkatalog, Produktakte, gerendertes Video).
//
// Diese Module sind lizenzpflichtig: Sie sind fuer keinen Mandanten sichtbar,
// solange sie nicht im GA-Portal freigeschaltet wurden (features.licensed_modules).
// Eingespielt wird die Lizenz in securityModules.ts → applyLicensedCatalogModules,
// serverseitig gespiegelt in api/_modulePlan.js → visibleModules.
//
// Die Lernbloecke und der Quizpool liegen in catalog2026Content.ts.

import type { SecurityModuleMeta } from "./securityModules";

/** Produktwelt eines Katalogmoduls – Gruppierung im GA-Portal und Vorschlag fuer Themen. */
export type CatalogWorld = "Security" | "Privacy" | "Safety" | "Compliance" | "Quality" | "Environment" | "Automotive" | "Aerospace";

export const CATALOG_2026_WORLD_ORDER: CatalogWorld[] = ["Security", "Privacy", "Safety", "Compliance", "Quality", "Environment", "Automotive", "Aerospace"];

/** Anzeigename der Produktwelt (zugleich Vorschlag fuer den Themen-Namen im Mandanten). */
export const CATALOG_2026_WORLD_LABEL: Record<CatalogWorld, string> = {
  Security: "Security Awareness",
  Privacy: "Datenschutz & DSGVO",
  Safety: "Safety & Arbeitsschutz",
  Compliance: "Compliance & Integrität",
  Quality: "Qualität & Prozesse",
  Environment: "Umwelt & Energie",
  Automotive: "Automotive",
  Aerospace: "Aerospace"
};

export const CATALOG_2026_WORLD_COLOR: Record<CatalogWorld, string> = {
  Security: "#1B6AB1",
  Privacy: "#4338CA",
  Safety: "#f97316",
  Compliance: "#7C3AED",
  Quality: "#CA8A04",
  Environment: "#0d9488",
  Automotive: "#e11d48",
  Aerospace: "#0ea5e9"
};

/** Modul-ID → Produktwelt. */
export const CATALOG_2026_WORLD_BY_ID: Record<string, CatalogWorld> = {
  "sec-01a-phishing-erkennen": "Security",
  "sec-01b-bec-anhaenge-und-meldeweg": "Security",
  "sec-02a-passwoerter-und-passwortmanager": "Security",
  "sec-02b-mfa-und-kontowiederherstellung": "Security",
  "sec-03a-social-engineering-telefon-und-chat": "Security",
  "sec-03b-physical-security-und-usb": "Security",
  "sec-04a-homeoffice-und-mobile-arbeit": "Security",
  "sec-04b-cloud-freigaben-und-datentransfer": "Security",
  "sec-05a-ki-daten-und-freigegebene-tools": "Security",
  "sec-05b-ki-ergebnisse-und-menschliche-kontrolle": "Security",
  "saf-01a-gefahren-erkennen-und-stoppen": "Safety",
  "saf-01b-verantwortung-und-sichere-kommunikation": "Safety",
  "saf-02-gesundheit-ergonomie-und-belastung": "Safety",
  "saf-03a-psa-auswahl-und-anwendung": "Safety",
  "saf-03b-gefahrstoffe-sicher-handhaben": "Safety",
  "saf-04a-brandschutz-und-evakuierung": "Safety",
  "saf-04b-erste-hilfe-und-notfallkommunikation": "Safety",
  "saf-05a-werkstatt-und-handwerkzeuge": "Safety",
  "saf-05b-maschinen-und-energieisolierung": "Safety",
  "com-01-code-of-conduct": "Compliance",
  "com-02-hinweisgeberschutz": "Compliance",
  "com-03-korruptionspraevention": "Compliance",
  "com-04-interessenkonflikte": "Compliance",
  "com-05a-kartellrecht": "Compliance",
  "com-05b-geldwaeschepraevention": "Compliance",
  "env-01-iso-14001-umweltmanagement": "Environment",
  "env-02-iso-50001-energiemanagement": "Environment",
  "env-03a-abfalltrennung-und-kreislaufwirtschaft": "Environment",
  "env-03b-umweltgefaehrdende-stoffe-und-leckagen": "Environment",
  "env-04-energieeffizienz-im-betrieb": "Environment",
  "env-05-nachhaltigkeit-und-belastbare-aussagen": "Environment",
  "aut-01a-tisax-und-isa-grundlagen": "Automotive",
  "aut-01b-isa-nachweise-und-wirksamkeit": "Automotive",
  "aut-02a-prototypenschutz-im-lebenszyklus": "Automotive",
  "aut-02b-prototypentransport-und-veranstaltungen": "Automotive",
  "aut-03-schutzklassen-und-zugriffssteuerung": "Automotive",
  "aut-04a-lieferanten-onboarding-und-sicherheitsanforderungen": "Automotive",
  "aut-04b-lieferkettenvorfaelle-und-kontinuitaet": "Automotive",
  "aut-05-iatf-qualitaetsbewusstsein": "Automotive",
  "aer-01a-en-9100-qms-grundlagen": "Aerospace",
  "aer-01b-risiko-aenderung-und-human-factors": "Aerospace",
  "aer-02-fod-praevention": "Aerospace",
  "aer-03-counterfeit-parts-prevention": "Aerospace",
  "aer-04a-konfigurationsidentifikation-und-baselines": "Aerospace",
  "aer-04b-aenderungssteuerung-und-konfigurationsstatus": "Aerospace",
  "aer-05a-rueckverfolgbarkeit-und-nichtkonformitaet": "Aerospace",
  "aer-05b-produktsicherheit-und-ethische-meldung": "Aerospace",
  "sec-06-informationssicherheit-im-arbeitsalltag": "Security",
  "env-06-umwelt-und-energie-im-arbeitsalltag": "Environment",
  "priv-01-datenschutz-im-arbeitsalltag": "Privacy",
};

/**
 * Themenblock innerhalb einer Produktwelt – die feinere Gliederung im GA-Portal.
 * Die Katalog-IDs tragen die Paarung ohnehin (sec-01a/sec-01b gehoeren zusammen);
 * hier bekommt sie einen Namen, damit Modul-Editor und Freischaltung nicht bei
 * zwoelf Zeilen am Stueck landen. Reine Anzeige-Gliederung: Lizenz, Preis und
 * Trainingsplaene haengen unveraendert an der einzelnen Modul-ID.
 */
export type CatalogTheme = { id: string; label: string; world: CatalogWorld; moduleIds: string[] };

export const CATALOG_2026_THEMES: CatalogTheme[] = [
  { id: "sec-phishing", world: "Security", label: "Phishing & E-Mail-Betrug", moduleIds: ["sec-01a-phishing-erkennen", "sec-01b-bec-anhaenge-und-meldeweg"] },
  { id: "sec-konten", world: "Security", label: "Konten & Zugänge", moduleIds: ["sec-02a-passwoerter-und-passwortmanager", "sec-02b-mfa-und-kontowiederherstellung"] },
  { id: "sec-manipulation", world: "Security", label: "Social Engineering & Zutritt", moduleIds: ["sec-03a-social-engineering-telefon-und-chat", "sec-03b-physical-security-und-usb"] },
  { id: "sec-mobil", world: "Security", label: "Mobiles Arbeiten & Cloud", moduleIds: ["sec-04a-homeoffice-und-mobile-arbeit", "sec-04b-cloud-freigaben-und-datentransfer"] },
  { id: "sec-ki", world: "Security", label: "KI-Nutzung", moduleIds: ["sec-05a-ki-daten-und-freigegebene-tools", "sec-05b-ki-ergebnisse-und-menschliche-kontrolle"] },
  { id: "sec-kern", world: "Security", label: "Kernmodul für alle", moduleIds: ["sec-06-informationssicherheit-im-arbeitsalltag"] },

  { id: "priv-grundlagen", world: "Privacy", label: "Grundlagen & Vorfälle", moduleIds: ["priv-01-datenschutz-im-arbeitsalltag"] },

  { id: "saf-gefaehrdung", world: "Safety", label: "Gefährdungen & Verantwortung", moduleIds: ["saf-01a-gefahren-erkennen-und-stoppen", "saf-01b-verantwortung-und-sichere-kommunikation"] },
  { id: "saf-gesundheit", world: "Safety", label: "Gesundheit & Ergonomie", moduleIds: ["saf-02-gesundheit-ergonomie-und-belastung"] },
  { id: "saf-schutz", world: "Safety", label: "PSA & Gefahrstoffe", moduleIds: ["saf-03a-psa-auswahl-und-anwendung", "saf-03b-gefahrstoffe-sicher-handhaben"] },
  { id: "saf-notfall", world: "Safety", label: "Brandschutz & Erste Hilfe", moduleIds: ["saf-04a-brandschutz-und-evakuierung", "saf-04b-erste-hilfe-und-notfallkommunikation"] },
  { id: "saf-technik", world: "Safety", label: "Werkstatt & Maschinen", moduleIds: ["saf-05a-werkstatt-und-handwerkzeuge", "saf-05b-maschinen-und-energieisolierung"] },

  { id: "com-verhalten", world: "Compliance", label: "Verhalten & Meldewege", moduleIds: ["com-01-code-of-conduct", "com-02-hinweisgeberschutz"] },
  { id: "com-integritaet", world: "Compliance", label: "Korruption & Interessenkonflikte", moduleIds: ["com-03-korruptionspraevention", "com-04-interessenkonflikte"] },
  { id: "com-markt", world: "Compliance", label: "Kartellrecht & Geldwäsche", moduleIds: ["com-05a-kartellrecht", "com-05b-geldwaeschepraevention"] },

  { id: "env-systeme", world: "Environment", label: "Managementsysteme (ISO 14001/50001)", moduleIds: ["env-01-iso-14001-umweltmanagement", "env-02-iso-50001-energiemanagement"] },
  { id: "env-stoffe", world: "Environment", label: "Abfall & umweltgefährdende Stoffe", moduleIds: ["env-03a-abfalltrennung-und-kreislaufwirtschaft", "env-03b-umweltgefaehrdende-stoffe-und-leckagen"] },
  { id: "env-energie", world: "Environment", label: "Energieeffizienz & Nachhaltigkeit", moduleIds: ["env-04-energieeffizienz-im-betrieb", "env-05-nachhaltigkeit-und-belastbare-aussagen"] },
  { id: "env-kern", world: "Environment", label: "Kernmodul für alle", moduleIds: ["env-06-umwelt-und-energie-im-arbeitsalltag"] },

  { id: "aut-tisax", world: "Automotive", label: "TISAX & VDA ISA", moduleIds: ["aut-01a-tisax-und-isa-grundlagen", "aut-01b-isa-nachweise-und-wirksamkeit"] },
  { id: "aut-prototypen", world: "Automotive", label: "Prototypenschutz", moduleIds: ["aut-02a-prototypenschutz-im-lebenszyklus", "aut-02b-prototypentransport-und-veranstaltungen"] },
  { id: "aut-zugriff", world: "Automotive", label: "Schutzklassen & Zugriff", moduleIds: ["aut-03-schutzklassen-und-zugriffssteuerung"] },
  { id: "aut-lieferkette", world: "Automotive", label: "Lieferkette", moduleIds: ["aut-04a-lieferanten-onboarding-und-sicherheitsanforderungen", "aut-04b-lieferkettenvorfaelle-und-kontinuitaet"] },
  { id: "aut-qualitaet", world: "Automotive", label: "Qualitätsbewusstsein (IATF)", moduleIds: ["aut-05-iatf-qualitaetsbewusstsein"] },

  { id: "aer-qms", world: "Aerospace", label: "EN 9100 & Human Factors", moduleIds: ["aer-01a-en-9100-qms-grundlagen", "aer-01b-risiko-aenderung-und-human-factors"] },
  { id: "aer-teile", world: "Aerospace", label: "FOD & Counterfeit Parts", moduleIds: ["aer-02-fod-praevention", "aer-03-counterfeit-parts-prevention"] },
  { id: "aer-konfiguration", world: "Aerospace", label: "Konfigurationsmanagement", moduleIds: ["aer-04a-konfigurationsidentifikation-und-baselines", "aer-04b-aenderungssteuerung-und-konfigurationsstatus"] },
  { id: "aer-nachweis", world: "Aerospace", label: "Rückverfolgbarkeit & Produktsicherheit", moduleIds: ["aer-05a-rueckverfolgbarkeit-und-nichtkonformitaet", "aer-05b-produktsicherheit-und-ethische-meldung"] },
];

/**
 * Modul-ID -> Themenblock, aus CATALOG_2026_THEMES abgeleitet. Bewusst keine
 * zweite von Hand gepflegte Tabelle: sonst laufen Zuordnung und Reihenfolge
 * auseinander, sobald ein Modul dazukommt.
 */
export const CATALOG_2026_THEME_BY_MODULE: Record<string, string> = Object.fromEntries(
  CATALOG_2026_THEMES.flatMap(theme => theme.moduleIds.map(id => [id, theme.id])),
);

export const CATALOG_2026_MODULES: SecurityModuleMeta[] = [
  {
    id: "sec-01a-phishing-erkennen",
    topicId: "sec-01a-phishing-erkennen",
    title: "Phishing erkennen und prüfen",
    shortTitle: "Phishing erkennen und prüfen",
    description: "Ein professionelles Layout beweist nichts. Entscheidend sind Absender, Kontext, Ziel und der unabhängige Gegencheck.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "🎣",
    focusAreas: [
      "Phishing unabhängig vom Erscheinungsbild erkennen",
      "Absender und Linkziel technisch plausibilisieren",
      "Dringlichkeit und ungewöhnliche Aufforderungen bewerten",
      "verdächtige Nachrichten sicher melden"
    ],
    scenario: "Die angebliche Paketbenachrichtigung. Ein QR-Code verspricht die Korrektur einer Lieferadresse.",
    level: "basis"
  },
  {
    id: "sec-01b-bec-anhaenge-und-meldeweg",
    topicId: "sec-01b-bec-anhaenge-und-meldeweg",
    title: "BEC, Anhänge und sichere Zahlungsfreigaben",
    shortTitle: "BEC, Anhänge und sichere Zahlungs…",
    description: "Angreifer brauchen nicht immer Schadsoftware. Manchmal reicht eine glaubwürdige Bitte um eine kleine Prozessausnahme.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "💸",
    focusAreas: [
      "BEC-Muster und Kontenübernahmen erkennen",
      "Zahlungs- und Stammdatenänderungen sicher bestätigen",
      "Dateityp und Herkunft von Anhängen bewerten",
      "nach Fehlhandlung schnell und beweissicher reagieren"
    ],
    scenario: "Neue Bankverbindung des Lieferanten. Kurz vor Zahlungseingang kommt eine plausible Änderungsmitteilung.",
    level: "basis"
  },
  {
    id: "sec-02a-passwoerter-und-passwortmanager",
    topicId: "sec-02a-passwoerter-und-passwortmanager",
    title: "Passwörter und Passwortmanager",
    shortTitle: "Passwörter und Passwortmanager",
    description: "Das stärkste Passwort nützt wenig, wenn es mehrfach verwendet, geteilt oder außerhalb des vorgesehenen Managers gespeichert wird.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "🔑",
    focusAreas: [
      "sichere und einzigartige Zugangsdaten verwenden",
      "Passwortmanager korrekt einsetzen",
      "Weitergabe und unsichere Speicherung vermeiden",
      "kompromittierte Zugänge geordnet absichern"
    ],
    scenario: "Der neue Projektzugang. Ein Team möchte ein Kundenportal gemeinsam nutzen.",
    level: "basis"
  },
  {
    id: "sec-02b-mfa-und-kontowiederherstellung",
    topicId: "sec-02b-mfa-und-kontowiederherstellung",
    title: "MFA, Push-Fatigue und Kontowiederherstellung",
    shortTitle: "MFA, Push-Fatigue und Kontowieder…",
    description: "Mehrfaktor schützt nur dann, wenn unerwartete Freigaben abgelehnt und Wiederherstellungswege genauso streng behandelt werden.",
    duration: "3 Min.",
    color: "#1B6AB1",
    icon: "📲",
    focusAreas: [
      "MFA-Anfragen sicher beurteilen",
      "Push-Fatigue und Code-Phishing erkennen",
      "Wiederherstellung und Gerätewechsel kontrolliert durchführen",
      "MFA-Vorfälle schnell melden"
    ],
    scenario: "Unerwartete Push-Anfrage. Mehrere MFA-Freigaben erscheinen, obwohl keine Anmeldung läuft.",
    level: "basis"
  },
  {
    id: "sec-03a-social-engineering-telefon-und-chat",
    topicId: "sec-03a-social-engineering-telefon-und-chat",
    title: "Social Engineering über Telefon und Chat",
    shortTitle: "Social Engineering über Telefon u…",
    description: "Hilfsbereitschaft ist kein Fehler. Gefährlich wird sie, wenn Identität und Berechtigung nicht unabhängig geprüft werden.",
    duration: "3 Min.",
    color: "#1B6AB1",
    icon: "📞",
    focusAreas: [
      "psychologische Manipulation erkennen",
      "Identität unabhängig verifizieren",
      "sensible Auskünfte und Handlungen begrenzen",
      "verdächtige Kontakte melden"
    ],
    scenario: "Der angebliche Service Desk. Ein Anrufer kennt Namen, Abteilung und ein aktuelles Projekt.",
    level: "basis"
  },
  {
    id: "sec-03b-physical-security-und-usb",
    topicId: "sec-03b-physical-security-und-usb",
    title: "Physical Security, Tailgating und USB",
    shortTitle: "Physical Security, Tailgating und…",
    description: "Informationssicherheit endet nicht am Bildschirm. Türen, Besucher, Ausdrucke und Datenträger sind Teil derselben Schutzkette.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "🚪",
    focusAreas: [
      "unberechtigten Zutritt erkennen und sicher ansprechen",
      "Fundmedien ohne Eigenversuch behandeln",
      "Arbeitsplatz und Geräte physisch schützen",
      "Vorfälle über den richtigen Kanal melden"
    ],
    scenario: "Die Person an der Seitentür. Jemand mit Firmenkleidung trägt Kartons und bittet, die Tür offenzuhalten.",
    level: "basis"
  },
  {
    id: "sec-04a-homeoffice-und-mobile-arbeit",
    topicId: "sec-04a-homeoffice-und-mobile-arbeit",
    title: "Sicheres Homeoffice und mobile Arbeit",
    shortTitle: "Sicheres Homeoffice und mobile Ar…",
    description: "Außerhalb des Büros verschwinden die Regeln nicht. Sichtschutz, Gesprächsumgebung, Geräte und Verbindungen werden nur schwieriger kontrollierbar.",
    duration: "3 Min.",
    color: "#1B6AB1",
    icon: "🏠",
    focusAreas: [
      "mobile Arbeitsumgebungen vor Beginn bewerten",
      "Geräte und Gespräche vor Dritten schützen",
      "zugelassene Verbindungen und Zugänge verwenden",
      "Verlust und Auffälligkeiten sofort melden"
    ],
    scenario: "Videocall im Zug. Der Termin lässt sich nicht verschieben, rundherum sitzen Reisende.",
    level: "basis"
  },
  {
    id: "sec-04b-cloud-freigaben-und-datentransfer",
    topicId: "sec-04b-cloud-freigaben-und-datentransfer",
    title: "Cloud-Freigaben und sicherer Datentransfer",
    shortTitle: "Cloud-Freigaben und sicherer Date…",
    description: "Die häufigste Cloud-Panne ist kein Hack. Es ist eine zu breite Freigabe, ein falscher Empfänger oder eine Datei am falschen Ort.",
    duration: "3 Min.",
    color: "#1B6AB1",
    icon: "☁️",
    focusAreas: [
      "Daten vor dem Teilen klassifizieren",
      "Empfänger und Berechtigung korrekt wählen",
      "Freigaben zeitlich und sachlich begrenzen",
      "Fehlfreigaben schnell zurücknehmen und melden"
    ],
    scenario: "Externer Projektordner. Ein Lieferant benötigt für drei Wochen Zugriff auf ausgewählte Unterlagen.",
    level: "basis"
  },
  {
    id: "sec-05a-ki-daten-und-freigegebene-tools",
    topicId: "sec-05a-ki-daten-und-freigegebene-tools",
    title: "KI-Tools: Daten schützen und Werkzeuge freigeben",
    shortTitle: "KI-Tools: Daten schützen und Werk…",
    description: "Ein Prompt ist eine Datenübertragung. Deshalb beginnt sichere KI-Nutzung vor der Eingabe, nicht erst bei der Ausgabe.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "🤖",
    focusAreas: [
      "KI-Eingaben als Datenübertragung bewerten",
      "freigegebene Werkzeuge und Konten verwenden",
      "sensible Daten vor Eingabe entfernen oder vermeiden",
      "unsichere Nutzung und Datenabfluss melden"
    ],
    scenario: "Protokoll zusammenfassen. Ein langes Kundengespräch soll schnell in Stichpunkte verwandelt werden.",
    level: "basis"
  },
  {
    id: "sec-05b-ki-ergebnisse-und-menschliche-kontrolle",
    topicId: "sec-05b-ki-ergebnisse-und-menschliche-kontrolle",
    title: "KI-Ergebnisse prüfen und verantwortlich verwenden",
    shortTitle: "KI-Ergebnisse prüfen und verantwo…",
    description: "Eine flüssige Antwort ist kein Beleg. KI kann plausibel klingen, Quellen erfinden und unsichere Empfehlungen mit großer Sicherheit präsentieren.",
    duration: "4 Min.",
    color: "#1B6AB1",
    icon: "🔍",
    focusAreas: [
      "KI-Ausgaben nach Risiko prüfen",
      "Quellen und Fakten unabhängig verifizieren",
      "menschliche Verantwortung und Freigaben erhalten",
      "kritische Nutzung nachvollziehbar dokumentieren"
    ],
    scenario: "Erfundene Rechtsquelle. Eine KI nennt einen überzeugenden Paragraphen für eine Kundenantwort.",
    level: "basis"
  },
  {
    id: "saf-01a-gefahren-erkennen-und-stoppen",
    topicId: "saf-01a-gefahren-erkennen-und-stoppen",
    title: "Gefahren erkennen und Arbeit sicher stoppen",
    shortTitle: "Gefahren erkennen und Arbeit sich…",
    description: "Unfälle entstehen selten aus einem einzigen Fehler. Meist werden mehrere schwache Signale übersehen, bis keine Sicherheitsreserve mehr bleibt.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "⚠️",
    focusAreas: [
      "Gefahren vor Tätigkeitsbeginn erkennen",
      "Veränderungen während der Arbeit neu bewerten",
      "Arbeit bei unklarer Sicherheit stoppen",
      "Gefahren absichern und wirksam melden"
    ],
    scenario: "Wasser auf dem Verkehrsweg. Eine Leckage macht den Hauptweg rutschig.",
    level: "basis"
  },
  {
    id: "saf-01b-verantwortung-und-sichere-kommunikation",
    topicId: "saf-01b-verantwortung-und-sichere-kommunikation",
    title: "Verantwortung, Unterweisung und sichere Kommunikation",
    shortTitle: "Verantwortung, Unterweisung und s…",
    description: "Sicherheit scheitert häufig nicht am fehlenden Wissen, sondern an unklaren Rollen, stillen Annahmen und nicht bestätigten Übergaben.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "📢",
    focusAreas: [
      "eigene Rolle und Grenzen erkennen",
      "Arbeitsaufträge sicher rückbestätigen",
      "Übergaben und Fremdfirmen koordinieren",
      "Abweichungen ohne Repressalien ansprechen"
    ],
    scenario: "Schichtwechsel an einer Anlage. Eine Störung ist nur teilweise behoben.",
    level: "basis"
  },
  {
    id: "saf-02-gesundheit-ergonomie-und-belastung",
    topicId: "saf-02-gesundheit-ergonomie-und-belastung",
    title: "Gesundheit, Ergonomie und Belastung",
    shortTitle: "Gesundheit, Ergonomie und Belastu…",
    description: "Gesundheitsrisiken sind nicht immer laut oder plötzlich. Fehlbelastung, Müdigkeit und Dauerstress wirken oft schleichend – und trotzdem messbar.",
    duration: "4 Min.",
    color: "#f97316",
    icon: "🩺",
    focusAreas: [
      "körperliche und psychische Belastungen früh erkennen",
      "Arbeitsplatz und Arbeitsweise ergonomisch anpassen",
      "Müdigkeit und Überlastung sicher ansprechen",
      "Unterstützungsangebote rechtzeitig nutzen"
    ],
    scenario: "Schmerzen bei wiederholter Montage. Nach mehreren Schichten treten Beschwerden in Schulter und Hand auf.",
    level: "basis"
  },
  {
    id: "saf-03a-psa-auswahl-und-anwendung",
    topicId: "saf-03a-psa-auswahl-und-anwendung",
    title: "Persönliche Schutzausrüstung richtig verwenden",
    shortTitle: "Persönliche Schutzausrüstung rich…",
    description: "PSA ist die letzte Barriere – nicht die erste. Sie wirkt nur, wenn sie zur Gefahr passt, intakt ist und tatsächlich richtig getragen wird.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "🥽",
    focusAreas: [
      "PSA als letzte Schutzebene einordnen",
      "vorgeschriebene PSA passend auswählen",
      "vor Nutzung Zustand und Passform prüfen",
      "defekte oder ungeeignete PSA sicher aussondern"
    ],
    scenario: "Beschlagene Schutzbrille. Die Brille wird für bessere Sicht kurz hochgeschoben.",
    level: "basis"
  },
  {
    id: "saf-03b-gefahrstoffe-sicher-handhaben",
    topicId: "saf-03b-gefahrstoffe-sicher-handhaben",
    title: "Gefahrstoffe sicher erkennen und handhaben",
    shortTitle: "Gefahrstoffe sicher erkennen und…",
    description: "Geruch, Farbe und Erfahrung reichen nicht aus. Maßgeblich sind Kennzeichnung, Sicherheitsdaten, Betriebsanweisung und die festgelegten Schutzmaßnahmen.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "☣️",
    focusAreas: [
      "Gefahrstoffinformationen korrekt lesen",
      "Expositionswege und Schutzmaßnahmen zuordnen",
      "Stoffe sicher lagern und verwenden",
      "Leckagen und Exposition richtig behandeln"
    ],
    scenario: "Unbeschriftete Sprühflasche. Der Inhalt wird häufig benutzt, aber das Etikett fehlt.",
    level: "basis"
  },
  {
    id: "saf-04a-brandschutz-und-evakuierung",
    topicId: "saf-04a-brandschutz-und-evakuierung",
    title: "Brandschutz und sichere Evakuierung",
    shortTitle: "Brandschutz und sichere Evakuieru…",
    description: "Im Brandfall ist die erste Minute zu kurz für Improvisation. Alarm, Fluchtweg und Sammelstelle müssen vorher bekannt sein.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "🔥",
    focusAreas: [
      "Brandgefahren und blockierte Rettungswege erkennen",
      "Alarmierung und Evakuierung korrekt auslösen",
      "Grenzen eigener Löschversuche beachten",
      "an der Sammelstelle verlässlich mitwirken"
    ],
    scenario: "Rauch aus einem Papierkorb. Der Brand wirkt noch klein.",
    level: "basis"
  },
  {
    id: "saf-04b-erste-hilfe-und-notfallkommunikation",
    topicId: "saf-04b-erste-hilfe-und-notfallkommunikation",
    title: "Erste Hilfe und Notfallkommunikation",
    shortTitle: "Erste Hilfe und Notfallkommunikat…",
    description: "Erste Hilfe verlangt keine perfekte Diagnose. Sie verlangt Eigenschutz, einen frühen Notruf und die richtigen lebensrettenden Maßnahmen.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "⛑️",
    focusAreas: [
      "Notfallsituation und Eigenschutz schnell bewerten",
      "Rettungskette ohne Verzögerung aktivieren",
      "Ersthelfende und AED gezielt einbinden",
      "Notfallinformationen klar übergeben"
    ],
    scenario: "Bewusstlose Person. Eine Person reagiert nicht auf Ansprache.",
    level: "basis"
  },
  {
    id: "saf-05a-werkstatt-und-handwerkzeuge",
    topicId: "saf-05a-werkstatt-und-handwerkzeuge",
    title: "Werkstattsicherheit und Handwerkzeuge",
    shortTitle: "Werkstattsicherheit und Handwerkz…",
    description: "Ein bekanntes Werkzeug wird nicht automatisch sicher. Zustand, Werkstück, Umgebung und richtige Anwendung müssen jedes Mal zusammenpassen.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "🔧",
    focusAreas: [
      "geeignetes Werkzeug und Verfahren auswählen",
      "Werkzeug und Umgebung vor Nutzung prüfen",
      "Werkstücke und Gefahrenbereich sichern",
      "Mängel kontrolliert aus dem Betrieb nehmen"
    ],
    scenario: "Beschädigtes Verlängerungskabel. Die Isolierung ist gequetscht, das Werkzeug funktioniert aber.",
    level: "basis"
  },
  {
    id: "saf-05b-maschinen-und-energieisolierung",
    topicId: "saf-05b-maschinen-und-energieisolierung",
    title: "Maschinensicherheit und Energieisolierung",
    shortTitle: "Maschinensicherheit und Energieis…",
    description: "Stillstand auf dem Bedienpanel bedeutet nicht energiefrei. Elektrische, hydraulische, pneumatische, thermische und gespeicherte Energie müssen kontrolliert werden.",
    duration: "3 Min.",
    color: "#f97316",
    icon: "⚙️",
    focusAreas: [
      "Maschinenzustand und Schutzeinrichtungen bewerten",
      "Störungen ohne Eingriff in Gefahrstellen behandeln",
      "Energieisolierung von normalem Stopp unterscheiden",
      "Freigaben und Wiederanlauf sicher koordinieren"
    ],
    scenario: "Verklemmtes Teil. Die Maschine steht, ein Werkstück ragt in die Gefahrstelle.",
    level: "basis"
  },
  {
    id: "com-01-code-of-conduct",
    topicId: "com-01-code-of-conduct",
    title: "Code of Conduct",
    shortTitle: "Code of Conduct",
    description: "Viele Regelverstöße beginnen nicht mit einem großen Plan. Sie beginnen mit einem kleinen Satz: Das machen hier doch alle so. Der Code of Conduct hilft genau in solchen Situationen. Er verbindet Gesetze, interne Regeln und gemeinsame Grundsätze zu einem verbindlichen Rahmen für das tägliche Handeln.",
    duration: "5 Min.",
    color: "#7C3AED",
    icon: "📜",
    focusAreas: [
      "den Code of Conduct als verbindlichen Entscheidungsrahmen im Arbeitsalltag nutzen",
      "kritische Situationen anhand eines kurzen Integritätschecks beurteilen",
      "Interessenkonflikte, mögliche Regelverstöße und Unsicherheiten über den richtigen Kanal ansprechen",
      "Verantwortung übernehmen, ohne selbst Ermittlungen durchzuführen"
    ],
    scenario: "Eine Einkäuferin soll drei Angebote bewerten. Eines stammt von einem Unternehmen, an dem ihr Bruder beteiligt ist.",
    level: "compliance"
  },
  {
    id: "com-02-hinweisgeberschutz",
    topicId: "com-02-hinweisgeberschutz",
    title: "Hinweisgeberschutz",
    shortTitle: "Hinweisgeberschutz",
    description: "Eine falsche Rechnung, manipulierte Prüfdaten oder ein schwerer Regelverstoß werden oft zuerst von Menschen bemerkt, die täglich mit dem Prozess arbeiten. Ob ein Schaden früh gestoppt wird, hängt dann von einer einfachen Frage ab: Gibt es einen sicheren Weg, die Beobachtung anzusprechen?",
    duration: "5 Min.",
    color: "#7C3AED",
    icon: "📣",
    focusAreas: [
      "einen Hinweis von einem Gerücht, einer öffentlichen Anschuldigung und einer gewöhnlichen Beschwerde unterscheiden",
      "interne und externe Meldewege korrekt einordnen",
      "eine sachliche, verwertbare Meldung erstellen, ohne selbst zu ermitteln",
      "Vertraulichkeit und Repressalienschutz im Arbeitsalltag beachten"
    ],
    scenario: "Ein Mitarbeiter bemerkt, dass Messwerte aus einer früheren Prüfung in ein aktuelles Protokoll übernommen wurden.",
    level: "compliance"
  },
  {
    id: "com-03-korruptionspraevention",
    topicId: "com-03-korruptionspraevention",
    title: "Korruptionsprävention",
    shortTitle: "Korruptionsprävention",
    description: "Ein Geschenk trägt selten die Aufschrift: Gegenleistung erwartet. Kritisch wird es oft erst durch den Zusammenhang. Eine Einladung kurz vor einer Vergabe wirkt anders als dieselbe Einladung nach Abschluss eines Projekts. Deshalb reicht die Frage nach dem Preis allein nicht aus.",
    duration: "6 Min.",
    color: "#7C3AED",
    icon: "🤝",
    focusAreas: [
      "zulässige Geschäftspflege von problematischen Vorteilen unterscheiden",
      "Risiken anhand von Zweck, Zeitpunkt, Wert, Häufigkeit und beteiligter Person bewerten",
      "mit Geschenken, Einladungen, Spenden und indirekten Vorteilen korrekt umgehen",
      "Verdachtsmomente dokumentieren und über den vorgesehenen Kanal klären"
    ],
    scenario: "Ein Lieferant lädt den zuständigen Einkäufer während einer laufenden Ausschreibung zu einem ausverkauften Sportereignis ein.",
    level: "compliance"
  },
  {
    id: "com-04-interessenkonflikte",
    topicId: "com-04-interessenkonflikte",
    title: "Interessenkonflikte offenlegen",
    shortTitle: "Interessenkonflikte offenlegen",
    description: "Ein Interessenkonflikt bedeutet nicht automatisch, dass jemand unehrlich ist. Er bedeutet, dass eine private Verbindung die dienstliche Entscheidung beeinflussen kann oder für andere so wirken könnte. Das Risiko entsteht vor allem dann, wenn der Konflikt verborgen bleibt.",
    duration: "3 Min.",
    color: "#7C3AED",
    icon: "⚖️",
    focusAreas: [
      "tatsächliche, potenzielle und scheinbare Interessenkonflikte erkennen",
      "typische Konflikte bei Vergaben, Nebentätigkeiten, Beziehungen und Beteiligungen einordnen",
      "einen Konflikt rechtzeitig offenlegen und sich aus der Entscheidung zurückziehen",
      "zwischen einem Konflikt und einem nachgewiesenen Fehlverhalten unterscheiden"
    ],
    scenario: "Eine Führungskraft erkennt unter den Bewerbungen eine enge Freundin.",
    level: "compliance"
  },
  {
    id: "com-05a-kartellrecht",
    topicId: "com-05a-kartellrecht",
    title: "Kartellrecht: im Wettbewerb sauber handeln",
    shortTitle: "Kartellrecht: im Wettbewerb saube…",
    description: "Ein unzulässiges Kartell braucht keinen unterschriebenen Vertrag. Schon eine abgestimmte Verhaltensweise oder ein informelles Einverständnis kann kritisch sein. Besonders gefährlich ist deshalb der Satz: Wir haben uns doch nur unterhalten.",
    duration: "4 Min.",
    color: "#7C3AED",
    icon: "🏁",
    focusAreas: [
      "besonders kritische Absprachen und Informationsaustausche erkennen",
      "sensible Kontakte auf Messen, in Verbänden und bei Ausschreibungen sicher behandeln",
      "ein unzulässiges Gespräch sichtbar beenden und korrekt dokumentieren",
      "kartellrechtliche Unsicherheiten vor einer Vereinbarung an die Rechts- oder Compliance-Stelle geben"
    ],
    scenario: "Auf einer Messe sagt ein Wettbewerber: Die Rohstoffkosten steigen. Wenn wir beide im nächsten Quartal fünf Prozent aufschlagen, zieht der Markt sicher mit.",
    level: "compliance"
  },
  {
    id: "com-05b-geldwaeschepraevention",
    topicId: "com-05b-geldwaeschepraevention",
    title: "Geldwäscheprävention: Auffälligkeiten erkennen",
    shortTitle: "Geldwäscheprävention: Auffälligke…",
    description: "Kriminelle Gelder werden selten mit einer ehrlichen Beschreibung überwiesen. Sie werden in scheinbar normale Geschäftsbeziehungen, Rechnungen und Zahlungswege eingebettet. Beschäftigte sollen deshalb nicht Kriminalfälle lösen. Sie sollen ungewöhnliche Muster erkennen und rechtzeitig an die zuständige Stelle geben.",
    duration: "4 Min.",
    color: "#7C3AED",
    icon: "💰",
    focusAreas: [
      "Zweck und risikobasierten Ansatz der Geldwäscheprävention erklären",
      "typische Auffälligkeiten bei Kunden, Eigentümern, Zahlungen und Transaktionen erkennen",
      "die eigene Rolle von der Entscheidung des Geldwäschebeauftragten trennen",
      "Verdachtsmomente intern eskalieren, ohne Betroffene zu informieren oder selbst zu ermitteln"
    ],
    scenario: "Ein neuer Kunde überweist deutlich mehr als vereinbart.",
    level: "compliance"
  },
  {
    id: "env-01-iso-14001-umweltmanagement",
    topicId: "env-01-iso-14001-umweltmanagement",
    title: "ISO 14001 und wirksames Umweltmanagement",
    shortTitle: "ISO 14001",
    description: "Ein Umweltmanagementsystem ist keine Sammlung grüner Absichten. Es verbindet relevante Umweltaspekte, Pflichten, Ziele, operative Steuerung und messbare Verbesserung.",
    duration: "3 Min.",
    color: "#0d9488",
    icon: "🌍",
    focusAreas: [
      "Umweltaspekt und Umweltauswirkung unterscheiden",
      "eigene Tätigkeit mit Umweltzielen verbinden",
      "operative Umweltvorgaben und Notfallregeln anwenden",
      "Abweichungen als Verbesserungsinformation melden"
    ],
    scenario: "Neues Reinigungsmittel. Ein Produkt soll schneller wirken und günstiger sein.",
    level: "compliance"
  },
  {
    id: "env-02-iso-50001-energiemanagement",
    topicId: "env-02-iso-50001-energiemanagement",
    title: "ISO 50001 und Energiemanagement",
    shortTitle: "ISO 50001",
    description: "Energieeffizienz entsteht nicht durch einzelne Spartipps, sondern durch Daten, wesentliche Energieeinsätze, klare Einflussgrößen und überprüfte Maßnahmen.",
    duration: "4 Min.",
    color: "#0d9488",
    icon: "⚡",
    focusAreas: [
      "Energieeinsatz und wesentliche Energieeinsätze einordnen",
      "relevante Einflussgrößen erkennen",
      "Energieabweichungen und Einsparideen melden",
      "Maßnahmen anhand geeigneter Daten bewerten"
    ],
    scenario: "Verbrauchssprung ohne Mehrproduktion. Der Wochenwert steigt deutlich, die Ausbringung bleibt gleich.",
    level: "compliance"
  },
  {
    id: "env-03a-abfalltrennung-und-kreislaufwirtschaft",
    topicId: "env-03a-abfalltrennung-und-kreislaufwirtschaft",
    title: "Abfalltrennung und Kreislaufwirtschaft",
    shortTitle: "Abfalltrennung und Kreislaufwirts…",
    description: "Ein falsch entsorgter Gegenstand kann eine ganze Fraktion entwerten. Entscheidend sind Vermeidung, richtige Zuordnung und ein nachvollziehbarer Entsorgungsweg.",
    duration: "3 Min.",
    color: "#0d9488",
    icon: "♻️",
    focusAreas: [
      "Abfallhierarchie auf Arbeitsentscheidungen anwenden",
      "Fraktionen nach lokaler Regel trennen",
      "kontaminierte oder unbekannte Abfälle stoppen",
      "Fehlwürfe und volle Gebinde richtig melden"
    ],
    scenario: "Ölverschmutzte Verpackung. Die Verpackung besteht aus Papier, ist aber mit Betriebsstoff verunreinigt.",
    level: "compliance"
  },
  {
    id: "env-03b-umweltgefaehrdende-stoffe-und-leckagen",
    topicId: "env-03b-umweltgefaehrdende-stoffe-und-leckagen",
    title: "Umweltgefährdende Stoffe und Leckagen",
    shortTitle: "Umweltgefährdende Stoffe und Leck…",
    description: "Eine kleine Leckage kann über Ablauf, Boden oder Regenwasser einen deutlich größeren Umweltbereich erreichen.",
    duration: "3 Min.",
    color: "#0d9488",
    icon: "🛢️",
    focusAreas: [
      "umweltrelevante Stoffe und Ausbreitungswege erkennen",
      "Lager- und Auffangsysteme korrekt nutzen",
      "Leckagen ohne Eigengefährdung begrenzen",
      "Umweltvorfälle vollständig melden"
    ],
    scenario: "Ölspur Richtung Ablauf. Ein Gabelstapler verliert Flüssigkeit in der Nähe eines Hofeinlaufs.",
    level: "compliance"
  },
  {
    id: "env-04-energieeffizienz-im-betrieb",
    topicId: "env-04-energieeffizienz-im-betrieb",
    title: "Energieeffizienz im Betriebsalltag",
    shortTitle: "Energieeffizienz im Betriebsalltag",
    description: "Effizienz bedeutet nicht, überall pauschal abzuschalten. Sie bedeutet, unnötigen Energieeinsatz zu erkennen, ohne Sicherheit, Qualität oder Anlagenzustand zu gefährden.",
    duration: "3 Min.",
    color: "#0d9488",
    icon: "💡",
    focusAreas: [
      "unnötige Energieverbräuche erkennen",
      "sichere betriebliche Einflussmöglichkeiten nutzen",
      "Leckagen und Fehlzustände melden",
      "Einsparmaßnahmen auf Nebenwirkungen und Wirkung prüfen"
    ],
    scenario: "Dauerhaft offene Hallentür. Materialverkehr und beheizte Halle stehen im Konflikt.",
    level: "compliance"
  },
  {
    id: "env-05-nachhaltigkeit-und-belastbare-aussagen",
    topicId: "env-05-nachhaltigkeit-und-belastbare-aussagen",
    title: "Nachhaltigkeit und belastbare Umweltaussagen",
    shortTitle: "Nachhaltigkeit und belastbare Umw…",
    description: "Eine gute Absicht ist noch kein belegbarer Umweltvorteil. Aussagen brauchen klaren Gegenstand, Systemgrenze, Datenquelle und verantwortliche Freigabe.",
    duration: "3 Min.",
    color: "#0d9488",
    icon: "🌿",
    focusAreas: [
      "Nachhaltigkeitswirkungen ganzheitlich betrachten",
      "Lebenszyklus und Zielkonflikte erkennen",
      "Umweltdaten und Aussagen kritisch prüfen",
      "unbelegte Claims vor Veröffentlichung stoppen"
    ],
    scenario: "Klimaneutrales Produkt. Marketing möchte eine weitreichende Aussage auf die Verpackung setzen.",
    level: "compliance"
  },
  {
    id: "aut-01a-tisax-und-isa-grundlagen",
    topicId: "aut-01a-tisax-und-isa-grundlagen",
    title: "TISAX und ISA: Grundlagen für Beschäftigte",
    shortTitle: "TISAX und ISA",
    description: "TISAX ist kein Logo für allgemeine IT-Sicherheit. Es ist ein Bewertungs- und Austauschmechanismus auf Basis definierter Prüfziele und eines konkreten Scopes.",
    duration: "4 Min.",
    color: "#e11d48",
    icon: "🔒",
    focusAreas: [
      "TISAX, ISA und ISMS voneinander unterscheiden",
      "Prüfziele und Scope auf den eigenen Bereich beziehen",
      "alltägliches Verhalten mit Nachweisen verbinden",
      "Assessmentfragen korrekt und transparent beantworten"
    ],
    scenario: "Prüfer fragt nach dem Prozess. Eine Person soll eine Zugriffsfreigabe erklären.",
    level: "compliance"
  },
  {
    id: "aut-01b-isa-nachweise-und-wirksamkeit",
    topicId: "aut-01b-isa-nachweise-und-wirksamkeit",
    title: "ISA-Nachweise und Wirksamkeit im Arbeitsalltag",
    shortTitle: "ISA-Nachweise und Wirksamkeit im…",
    description: "Ein Nachweis beweist nicht, dass ein Dokument existiert. Er zeigt, dass eine Anforderung verantwortlich umgesetzt, geprüft und bei Abweichung verbessert wird.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "📋",
    focusAreas: [
      "Kontrollziel von bloßer Aktivität unterscheiden",
      "geeignete Nachweise auswählen",
      "Stichproben und Ausnahmen nachvollziehbar erklären",
      "Korrekturen auf Wirksamkeit prüfen"
    ],
    scenario: "Zugriffsreview ohne Entscheidungen. Eine exportierte Benutzerliste liegt vor.",
    level: "compliance"
  },
  {
    id: "aut-02a-prototypenschutz-im-lebenszyklus",
    topicId: "aut-02a-prototypenschutz-im-lebenszyklus",
    title: "Prototypenschutz über den Lebenszyklus",
    shortTitle: "Prototypenschutz über den Lebensz…",
    description: "Ein Prototyp bleibt nicht nur in der Werkhalle schutzbedürftig. Beschaffung, Bearbeitung, Test, Foto, Transport, Veranstaltung und Entsorgung bilden eine durchgängige Schutzkette.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "🛠️",
    focusAreas: [
      "Prototypen und zugehörige Informationen erkennen",
      "Schutzbedarf über alle Lebensphasen erhalten",
      "Zugriff und Aufnahmen auf Need-to-know begrenzen",
      "Abweichungen und Verlust sofort melden"
    ],
    scenario: "Besucher im Versuchsbereich. Ein bekannter Kunde möchte spontan ein Teil ansehen.",
    level: "compliance"
  },
  {
    id: "aut-02b-prototypentransport-und-veranstaltungen",
    topicId: "aut-02b-prototypentransport-und-veranstaltungen",
    title: "Prototypentransport, Tests und Veranstaltungen",
    shortTitle: "Prototypentransport, Tests und Ve…",
    description: "Außerhalb des Werks treffen Prototypen auf öffentliche Sicht, wechselnde Dienstleister und schwer kontrollierbare Umgebungen. Deshalb braucht jede Bewegung eine geplante Schutzkette.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "🚛",
    focusAreas: [
      "Transport- und Veranstaltungsrisiken vorab bewerten",
      "Übergaben und Verwahrung lückenlos gestalten",
      "Sicht- und Aufnahmeschutz außerhalb des Standorts anwenden",
      "Schutzverletzungen sofort begrenzen und melden"
    ],
    scenario: "Panne auf öffentlicher Straße. Ein getarnter Versuchsträger kann nicht weiterfahren.",
    level: "compliance"
  },
  {
    id: "aut-03-schutzklassen-und-zugriffssteuerung",
    topicId: "aut-03-schutzklassen-und-zugriffssteuerung",
    title: "Schutzklassen und Zugriffssteuerung",
    shortTitle: "Schutzklassen und Zugriffssteueru…",
    description: "Eine Schutzklasse ist nur dann wirksam, wenn sie konkrete Folgen für Speicherung, Versand, Besprechung, Ausdruck, Zugriff und Löschung hat.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "🔐",
    focusAreas: [
      "Schutzklassen korrekt auf Informationen anwenden",
      "zulässige Verarbeitungswege auswählen",
      "Zugriffsrechte auf Bedarf begrenzen",
      "Fehlklassifizierung und Fehlzugriff melden"
    ],
    scenario: "Kundenzeichnung im Teamordner. Der Ordner ist für die gesamte Abteilung sichtbar.",
    level: "compliance"
  },
  {
    id: "aut-04a-lieferanten-onboarding-und-sicherheitsanforderungen",
    topicId: "aut-04a-lieferanten-onboarding-und-sicherheitsanforderungen",
    title: "Lieferanten-Onboarding und Sicherheitsanforderungen",
    shortTitle: "Lieferanten-Onboarding und Sicher…",
    description: "Ein Vertrag überträgt Arbeit, aber nicht die Verantwortung für Informationssicherheit. Risiken müssen vor Zugriff und Datenübergabe bewertet werden.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "🤝",
    focusAreas: [
      "Lieferanten nach Informations- und Prozessrisiko einstufen",
      "Sicherheitsanforderungen vor Beauftragung verankern",
      "Zugriff und Unterauftragnehmer kontrollieren",
      "Offboarding und Rückgabe nachweisen"
    ],
    scenario: "Lieferant ohne passenden TISAX-Scope. Ein Label ist vorhanden, deckt aber den geplanten Standort nicht ab.",
    level: "compliance"
  },
  {
    id: "aut-04b-lieferkettenvorfaelle-und-kontinuitaet",
    topicId: "aut-04b-lieferkettenvorfaelle-und-kontinuitaet",
    title: "Lieferkettenvorfälle und sichere Kontinuität",
    shortTitle: "Lieferkettenvorfälle und sichere…",
    description: "Ein Vorfall beim Lieferanten kann gleichzeitig Verfügbarkeit, Vertraulichkeit, Qualität und Lieferfähigkeit treffen. Frühzeitige Transparenz entscheidet über Schadensbegrenzung.",
    duration: "4 Min.",
    color: "#e11d48",
    icon: "🔗",
    focusAreas: [
      "Lieferantenvorfälle früh als eigenes Risiko behandeln",
      "Auswirkung auf Daten, Produktion und Kunden bewerten",
      "Notbetrieb kontrolliert freigeben",
      "Wiederanlauf und Lehren nachvollziehbar dokumentieren"
    ],
    scenario: "Ransomware beim Logistikdienstleister. Lieferstatus und Sendungsdaten sind nicht verfügbar.",
    level: "compliance"
  },
  {
    id: "aut-05-iatf-qualitaetsbewusstsein",
    topicId: "aut-05-iatf-qualitaetsbewusstsein",
    title: "IATF-orientiertes Qualitätsbewusstsein",
    shortTitle: "IATF-orientiertes Qualitätsbewuss…",
    description: "Automotive-Qualität bedeutet nicht nur fehlerfreie Endkontrolle. Prozesse müssen Fehler verhindern, Abweichungen begrenzen und Kundenanforderungen nachvollziehbar erfüllen.",
    duration: "3 Min.",
    color: "#e11d48",
    icon: "🎯",
    focusAreas: [
      "Prozess- und Kundenwirkung eigener Arbeit erkennen",
      "Abweichungen stoppen und korrekt behandeln",
      "Änderungen nur kontrolliert umsetzen",
      "Qualitätsaufzeichnungen wahrheitsgemäß führen"
    ],
    scenario: "Messwert knapp außerhalb Toleranz. Der nächste Prozessschritt wartet bereits.",
    level: "compliance"
  },
  {
    id: "aer-01a-en-9100-qms-grundlagen",
    topicId: "aer-01a-en-9100-qms-grundlagen",
    title: "EN 9100: Qualitätsmanagement in der Luftfahrt",
    shortTitle: "EN 9100",
    description: "In der Luftfahrt kann ein kleiner Dokumentations- oder Prozessfehler weit entfernt und lange nach der eigenen Arbeit sicherheitsrelevant werden.",
    duration: "4 Min.",
    color: "#0ea5e9",
    icon: "✈️",
    focusAreas: [
      "die besondere Wirkung von Luftfahrtqualität verstehen",
      "eigene Arbeit im Prozess und Produktlebenszyklus einordnen",
      "Abweichungen und Risiken früh eskalieren",
      "Aufzeichnungen vollständig und wahrheitsgemäß führen"
    ],
    scenario: "Unklare Zeichnungsrevision. Auf Arbeitsplatz und System werden unterschiedliche Stände angezeigt.",
    level: "compliance"
  },
  {
    id: "aer-01b-risiko-aenderung-und-human-factors",
    topicId: "aer-01b-risiko-aenderung-und-human-factors",
    title: "Risiko, Änderungen und Human Factors",
    shortTitle: "Risiko, Änderungen und Human Fact…",
    description: "Menschen machen nicht einfach Fehler. Bedingungen wie Müdigkeit, Unterbrechung, Übergabe und schlecht gestaltete Prozesse beeinflussen, wie wahrscheinlich Fehler werden.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🧠",
    focusAreas: [
      "Human-Factors-Risiken in Arbeitssituationen erkennen",
      "Unterbrechungen und Übergaben sicher beherrschen",
      "Änderungen vor Umsetzung risikobewerten",
      "Fehler und schwache Signale ohne Vertuschung melden"
    ],
    scenario: "Schichtwechsel mitten im Prüflauf. Eine Messreihe ist noch nicht abgeschlossen.",
    level: "compliance"
  },
  {
    id: "aer-02-fod-praevention",
    topicId: "aer-02-fod-praevention",
    title: "FOD Prevention",
    shortTitle: "FOD Prevention",
    description: "Ein loses Teil, eine Faser oder ein Werkzeug kann klein wirken und trotzdem ein Luftfahrzeug, Triebwerk oder System schwer beschädigen.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🧹",
    focusAreas: [
      "FOD-Quellen und gefährdete Zonen erkennen",
      "Werkzeuge und lose Teile kontrollieren",
      "Arbeitsplatz und Produkt vor Abschluss inspizieren",
      "fehlende Gegenstände oder FOD-Fund sofort eskalieren"
    ],
    scenario: "Ein Bit fehlt. Nach der Arbeit ist ein Werkzeugeinsatz nicht im Satz.",
    level: "compliance"
  },
  {
    id: "aer-03-counterfeit-parts-prevention",
    topicId: "aer-03-counterfeit-parts-prevention",
    title: "Counterfeit Parts Prevention",
    shortTitle: "Counterfeit Parts Prevention",
    description: "Ein Teil kann korrekt aussehen und trotzdem falsche Herkunft, veränderte Kennzeichnung oder nicht belegte Eigenschaften besitzen.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🔎",
    focusAreas: [
      "verdächtige und gefälschte Teile als Sicherheitsrisiko einordnen",
      "Beschaffungs- und Herkunftsnachweise prüfen",
      "Warnsignale im Wareneingang erkennen",
      "verdächtige Teile sichern und melden"
    ],
    scenario: "Obsoletes Bauteil vom Broker. Nur ein nicht autorisierter Händler kann kurzfristig liefern.",
    level: "compliance"
  },
  {
    id: "aer-04a-konfigurationsidentifikation-und-baselines",
    topicId: "aer-04a-konfigurationsidentifikation-und-baselines",
    title: "Konfigurationsidentifikation und Baselines",
    shortTitle: "Konfigurationsidentifikation und…",
    description: "Ohne eindeutige Konfiguration ist nicht belegbar, was gebaut, geprüft, geliefert oder später geändert wurde.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🗂️",
    focusAreas: [
      "Konfigurationseinheit und Baseline verstehen",
      "gültigen Stand vor Arbeit eindeutig bestimmen",
      "Produkt und Dokumentstand verknüpfen",
      "unklare oder widersprüchliche Konfiguration stoppen"
    ],
    scenario: "Alter Ausdruck am Arbeitsplatz. Die Papierzeichnung unterscheidet sich vom Systemstand.",
    level: "compliance"
  },
  {
    id: "aer-04b-aenderungssteuerung-und-konfigurationsstatus",
    topicId: "aer-04b-aenderungssteuerung-und-konfigurationsstatus",
    title: "Änderungssteuerung und Konfigurationsstatus",
    shortTitle: "Änderungssteuerung und Konfigurat…",
    description: "Eine technisch sinnvolle Änderung kann trotzdem unzulässig sein, wenn Wirkung, Genehmigung, Einführung und betroffener Umfang nicht kontrolliert sind.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🔄",
    focusAreas: [
      "Änderung von informeller Verbesserung unterscheiden",
      "Auswirkung und betroffenen Umfang bewerten",
      "Einführung und Wirksamkeit kontrollieren",
      "Konfigurationsstatus für Produkt und Dokument erhalten"
    ],
    scenario: "Software-Fix vor Auslieferung. Ein Fehler ist gefunden, die offizielle Änderung dauert länger.",
    level: "compliance"
  },
  {
    id: "aer-05a-rueckverfolgbarkeit-und-nichtkonformitaet",
    topicId: "aer-05a-rueckverfolgbarkeit-und-nichtkonformitaet",
    title: "Rückverfolgbarkeit und Nichtkonformität",
    shortTitle: "Rückverfolgbarkeit und Nichtkonfo…",
    description: "Rückverfolgbarkeit ist die Fähigkeit, einen betroffenen Umfang schnell und belastbar einzugrenzen – nicht nur eine Nummer auf einem Etikett.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🧾",
    focusAreas: [
      "Rückverfolgbarkeitsdaten vollständig erfassen",
      "Produktstatus eindeutig erkennen",
      "Nichtkonformität sicher sperren und eingrenzen",
      "Disposition und Freigabe nur autorisiert umsetzen"
    ],
    scenario: "Etikett nicht mehr lesbar. Ein Behälter enthält ähnliche Teile aus zwei Losen.",
    level: "compliance"
  },
  {
    id: "aer-05b-produktsicherheit-und-ethische-meldung",
    topicId: "aer-05b-produktsicherheit-und-ethische-meldung",
    title: "Produktsicherheit und ethische Meldung",
    shortTitle: "Produktsicherheit und ethische Me…",
    description: "Produktsicherheit endet nicht mit der eigenen Prozessgrenze. Wer ein mögliches unsicheres Ergebnis erkennt, muss es früh und ohne Druck zur Beschönigung eskalieren.",
    duration: "3 Min.",
    color: "#0ea5e9",
    icon: "🛡️",
    focusAreas: [
      "mögliche Produktsicherheitswirkung erkennen",
      "Sicherheits- und Qualitätsabweichung priorisiert eskalieren",
      "Fakten von Vermutung trennen",
      "Druck zur Falschangabe oder Nichtmeldung zurückweisen"
    ],
    scenario: "Mögliches Sicherheitsrisiko kurz vor Versand. Die technische Wirkung ist noch nicht abschließend geklärt.",
    level: "compliance"
  },
  {
    id: "sec-06-informationssicherheit-im-arbeitsalltag",
    topicId: "sec-06-informationssicherheit-im-arbeitsalltag",
    title: "Informationssicherheit im Arbeitsalltag",
    shortTitle: "Informationssicherheit",
    description: "Nicht Technik allein schützt Informationen. Entscheidend ist, wie Menschen klassifizieren, prüfen, freigeben und früh melden.",
    duration: "6 Min.",
    color: "#1B6AB1",
    icon: "🧭",
    focusAreas: [
      "Schutzbedarf anwenden",
      "Identitäten und Geräte sichern",
      "Angriffe stoppen",
      "nur freigegebene Wege nutzen",
      "Beobachtungen früh melden"
    ],
    scenario: "Der Fehlklick wird still „repariert“. Eine Person gibt auf einer gefälschten Seite Zugangsdaten ein.",
    level: "basis"
  },
  {
    id: "env-06-umwelt-und-energie-im-arbeitsalltag",
    topicId: "env-06-umwelt-und-energie-im-arbeitsalltag",
    title: "Umwelt und Energie im Arbeitsalltag",
    shortTitle: "Umwelt und Energie",
    description: "Ein Managementsystem wird nicht durch Ordner wirksam, sondern durch richtige Entscheidungen, frühe Meldungen und messbare Verbesserung.",
    duration: "6 Min.",
    color: "#0d9488",
    icon: "🌱",
    focusAreas: [
      "Umweltaspekte erkennen",
      "Energieverluste vermeiden",
      "sicher innerhalb von Vorgaben handeln",
      "Abweichungen und Ideen früh melden"
    ],
    scenario: "Energiesparen durch Regelbruch. Eine Absaugung wird ausgeschaltet, um Strom zu sparen.",
    level: "basis"
  },
  {
    id: "priv-01-datenschutz-im-arbeitsalltag",
    topicId: "priv-01-datenschutz-im-arbeitsalltag",
    title: "Datenschutz im Arbeitsalltag",
    shortTitle: "Datenschutz",
    description: "Datenschutz beginnt nicht bei Formularen. Er beginnt in dem Moment, in dem Informationen über Menschen geöffnet, geteilt oder ausgewertet werden.",
    duration: "12 Min.",
    color: "#4338CA",
    icon: "🪪",
    focusAreas: [
      "Personenbezug auch indirekt erkennen",
      "Zweckbindung und Datenminimierung anwenden",
      "Empfänger, Kanal und Freigabe vor dem Teilen prüfen",
      "Betroffenenanfragen sichern und weiterleiten",
      "mögliche Datenschutzvorfälle sofort melden"
    ],
    scenario: "Der Fehlversand wird still zurückgerufen. Eine Tabelle mit Kontaktdaten geht an die falsche externe Person.",
    level: "basis"
  }
];

export const CATALOG_2026_IDS: ReadonlySet<string> = new Set(CATALOG_2026_MODULES.map(m => m.id));

export function isCatalog2026Module(moduleId: string): boolean {
  return CATALOG_2026_IDS.has(moduleId);
}

/** Modul-IDs einer Produktwelt, in Katalogreihenfolge. */
export function catalog2026ModulesOfWorld(world: CatalogWorld): SecurityModuleMeta[] {
  return CATALOG_2026_MODULES.filter(m => CATALOG_2026_WORLD_BY_ID[m.id] === world);
}

/**
 * Module einer Produktwelt in ihre Themenbloecke zerlegt – die Gliederung, die
 * Modul-Editor und Freischaltung gemeinsam nutzen. Reihenfolge kommt aus
 * CATALOG_2026_THEMES; was dort nicht steht (Sicherheitsunterweisung,
 * Mandantenmodule), landet unbenannt am Ende statt zu verschwinden.
 */
export type ThemeGroup = { key: string; label: string | null; modules: SecurityModuleMeta[] };

export function groupByTheme(modules: SecurityModuleMeta[]): ThemeGroup[] {
  const byTheme = new Map<string, SecurityModuleMeta[]>();
  const rest: SecurityModuleMeta[] = [];
  for (const m of modules) {
    const themeId = CATALOG_2026_THEME_BY_MODULE[m.id];
    if (!themeId) { rest.push(m); continue; }
    const list = byTheme.get(themeId);
    if (list) list.push(m);
    else byTheme.set(themeId, [m]);
  }
  const groups: ThemeGroup[] = CATALOG_2026_THEMES
    .filter(theme => byTheme.has(theme.id))
    .map(theme => ({ key: theme.id, label: theme.label, modules: byTheme.get(theme.id) as SecurityModuleMeta[] }));
  if (rest.length > 0) groups.push({ key: "__rest__", label: null, modules: rest });
  return groups;
}
